// mapbox
import store from '@/store';
import { ref } from '@vue/composition-api';
import mapboxgl from 'mapbox-gl';
import { useService } from '../api/services';

// confgis
import ballPositionLayers from '../configs/ballPositionLayer';

export const useTournamentBall = () => {
    // services
    const { getLatestBallPosition } = useService();

    const tournamentBallObjects = ref<any[]>([]);

    // methods
    const initBallPositionSource = (map: mapboxgl.Map): void => {
        if (map.getSource('ball-position')) map.removeSource('ball-position');

        const ballPositionSource = {
            type: 'geojson',
            maxzoom: 16,
            buffer: 512,
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        };

        map.addSource('ball-position', ballPositionSource);
    };

    const initBallPositionLayers = (map: mapboxgl.Map): void => {
        ballPositionLayers.forEach((layer) => {
            map.addLayer(layer);
        });
    };

    const removeBallPositionLayers = (map: mapboxgl.Map): void => {
        ballPositionLayers.forEach((layer) => {
            map.removeLayer(layer);
        });
    };

    const generateBall = (ball: any): object => {
        return {
            id: ball.playerId,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [ball.y, ball.x],
            },
            properties: {
                title: ball.playerName,
                player: ball.playerName,
                isVisible: true,
                id: ball.playerId,
                labelState: 'on',
                color: ball.country == 'USA' ? '#ff0000' : '#0000ff',
                strokecolor: '#00ff00',
            },
        };
    };

    const generateTee = (ball: any): object => {
        return {
            id: ball.holeNo,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [ball.ty, ball.tx],
            },
            properties: {
                isVisible: true,
                id: ball.holeNo,
                labelState: 'on',
                color: '#000000',
                strokecolor: '#00ff00',
            },
        };
    };

    const generatePin = (ball: any): object => {
        return {
            id: ball.holeNo,
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [ball.py, ball.px],
            },
            properties: {
                isVisible: true,
                id: ball.holeNo,
                labelState: 'on',
                color: '#FFFFFF',
                strokecolor: '#000000',
            },
        };
    };

    const addBallPoint = (map: mapboxgl.Map, ball: any): void => {
        const source = map.getSource('ball-position');
        const features = source._data.features;
        const newFeature = generateBall(ball);
        const teeFeature = generateTee(ball);
        const pinFeature = generatePin(ball);
        features.push(newFeature);
        features.push(teeFeature);
        features.push(pinFeature);

        refreshBallPoint(map, ball);
    };

    const refreshBallPoint = (map: mapboxgl.Map, ball: any): void => {
        const source = map.getSource('ball-position');

        if (!source) return;

        const features = source._data.features;
        const featureIndex = features.findIndex((x) => {
            return x.properties.id === ball.id;
        });

        features[featureIndex] = generateBall(ball);

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const setAllTournamentBallVisibility = (
        map: mapboxgl.Map,
        isVisible: boolean,
    ): void => {
        if (isVisible === undefined) {
            isVisible = true;
        }
        const source = map.getSource('ball-position');
        const features = source._data.features;
        features.forEach((feature) => {
            feature.properties.isVisible = isVisible;
        });

        source.setData({
            type: 'FeatureCollection',
            features: features,
        });
    };

    const getTournamentBall = async (map: mapboxgl.Map) => {
        tournamentBallObjects.value = [];
        const ballObjs = await getLatestBallPosition(
            store.getters.currentCourseSchema.id,
        );
        if (ballObjs == null) {
            return;
        }

        ballObjs.forEach((ball) => {
            tournamentBallObjects.value.push(ball);
        });

        if (map.getLayer('ball-position-point')) {
            map.removeLayer('ball-position-point');
        }

        if (map.getLayer('ball-position-title')) {
            map.removeLayer('ball-position-title');
        }

        if (map.getSource('ball-position')) {
            map.removeSource('ball-position');
        }

        initBallPositionSource(map);
        initBallPositionLayers(map);

        tournamentBallObjects.value.forEach((ball) => {
            // if (ball.holeNo === 18 || ball.holeNo === 9) return;
            addBallPoint(map, ball);
        });
    };

    return {
        addBallPoint,
        refreshBallPoint,
        getTournamentBall,
        initBallPositionSource,
        initBallPositionLayers,
        removeBallPositionLayers,
        setAllTournamentBallVisibility,
    };
};
